'use client';

import { useRef, useCallback, useEffect } from 'react';

import comparison from 'string-comparison';

import { buildIdFromStepName } from '@avodah-inc/ui/utils';

export type HighlightTextFunction = ({
  mainElement,
  text,
  color,
  synonyms,
  workflowStep,
}: {
  mainElement: HTMLDivElement | null;
  text: string;
  color: string;
  synonyms: string[];
  workflowStep: string;
}) => void;

const isContextMatch = (nodeText: string, context: string) => {
  const limit = Math.max(0, nodeText.length - context.length);
  for (let i = 0; i <= limit; i++) {
    if (
      comparison.levenshtein.similarity(
        nodeText.slice(i, i + context.length),
        context,
      ) >= 0.7
    ) {
      return true;
    }
  }
  return false;
};

export const useContextHighlighter = (): {
  handleContextMapping: HighlightTextFunction;
  revertPreviousHighlight: (mainElement: HTMLDivElement | null) => void;
} => {
  const timeoutIds = useRef<NodeJS.Timeout[]>([]);

  useEffect(() => {
    return () => {
      timeoutIds.current.forEach(clearTimeout);
    };
  }, []);

  const revertPreviousHighlight = (mainElement: HTMLDivElement | null) => {
    const reversibleHighlights = mainElement?.querySelectorAll(
      '.reversible-highlight',
    );
    reversibleHighlights?.forEach((highlight) => {
      const originalText = highlight.textContent || '';
      highlight.outerHTML = originalText;
    });
  };

  const handleContextMapping: HighlightTextFunction = useCallback(
    ({ mainElement, text, color, synonyms, workflowStep }) => {
      if (!mainElement) return;
      const workflowElementId = buildIdFromStepName(workflowStep);
      const workflowElement = mainElement.querySelector(
        `#${workflowElementId}`,
      ) as unknown as HTMLDivElement | null;
      if (!workflowElement) return;

      timeoutIds.current.forEach(clearTimeout);
      timeoutIds.current = [];
      const context = text.trim() || '';
      const highlightContext = (node: Node) => {
        const treeWalker = document.createTreeWalker(
          node,
          NodeFilter.SHOW_TEXT,
          null,
        );
        let currentNode: Node | null = null;
        while ((currentNode = treeWalker.nextNode())) {
          const nodeText = currentNode.textContent?.replace(/\s+/g, ' ') || '';
          const contextMatch = isContextMatch(
            nodeText.toLowerCase().trim(),
            context.toLowerCase(),
          );
          if (contextMatch) {
            if (!currentNode.parentNode) {
              return;
            }
            const parentNode = currentNode.parentNode as HTMLElement;
            let highlightedText = nodeText;
            synonyms.forEach((synonym) => {
              const regex = new RegExp(synonym, 'gi');
              const replacement = `<span class="bg-gray-100 text-${color} reversible-highlight">$&</span>`;
              if (parentNode.innerHTML.includes(replacement)) {
                highlightedText = highlightedText.replace(regex, `$&`);
              } else {
                highlightedText = highlightedText.replace(regex, replacement);
              }
            });

            parentNode.innerHTML = highlightedText;
            const hasHighlightedChild = parentNode.querySelector(
              '.reversible-highlight',
            );
            if (hasHighlightedChild) {
              const directParent =
                hasHighlightedChild.parentNode as HTMLElement;
              directParent.style.marginTop = '-180px';
              directParent.style.paddingTop = '180px';
              directParent.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
              });
              // After scrolling, we set the margin and padding back to their original values.
              directParent.style.marginTop = '-180px';
              directParent.style.paddingTop = '180px';
            }

            const highlightedElements = parentNode.querySelectorAll(
              '.reversible-highlight',
            );
            highlightedElements.forEach((element) => {
              const adjacentNode = element.parentElement;
              if (!adjacentNode) return;
              adjacentNode.style.borderBottom = '1px solid blue';
              adjacentNode.style.transition = 'all 0.6s ease-in-out';
              const timeout = setTimeout(() => {
                adjacentNode.style.borderBottom = '';
              }, 2000);
              timeoutIds.current.push(timeout);
            });
            return;
          }
        }
      };
      revertPreviousHighlight(mainElement);
      workflowElement.querySelectorAll('p').forEach(highlightContext);
    },
    [],
  );

  return { handleContextMapping, revertPreviousHighlight };
};
